import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { format, differenceInCalendarDays } from "date-fns";
import {
  Typography,
  Box,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TablePagination,
  alpha,
  useTheme,
  Grid,
  Card,
  CircularProgress,
  Tooltip,
  Avatar
} from "@mui/material";
import {
  getTransactionsById,
  updateTransaction,
  deleteUserTransactions,
} from "../api/leads";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  EventNote as NoteIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccountCircle as UserIcon,
  Payments as PaymentsIcon
} from "@mui/icons-material";

export default function MyLead() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadsData, setLeadsData] = useState({});
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [reminderDate, setReminderDate] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);

  const headers = [
    { label: "Date", key: "date", sortable: true },
    { label: "Amount", key: "amount", sortable: true },
    { label: "Status", key: "status", sortable: false },
  ];

  // Sorting configuration state
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const navigate = useNavigate();

  const getData = async () => {
    setLoading(true);
    try {
      const data = await getTransactionsById(id);
      if (data.status) {
        setLeadsData(data.data);
      }
    } catch (error) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    const sortedData = [...leadsData.transactionIds].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setLeadsData({ ...leadsData, transactionIds: sortedData });
  };

  const paginatedData = leadsData?.transactionIds
    ? leadsData.transactionIds.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    : [];

  const openDialog = () => {
    setStatus(leadsData.status);
    setNotes(leadsData.notes || "");
    setReminderDate(
      leadsData.reminderDate ? leadsData.reminderDate.split("T")[0] : null
    );
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleUpdate = async () => {
    const updatePayload = {};
    if (status) updatePayload.status = status;
    if (notes) updatePayload.notes = notes;
    if (reminderDate) updatePayload.reminderDate = reminderDate;
    const response = await updateTransaction(leadsData._id, updatePayload);
    if (response.status) {
      getData();
    }
    closeDialog();
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      const data = await deleteUserTransactions(leadsData._id);
      if (data.status) {
        navigate("/payments-leads", { replace: true });
      }
    }
  };

  useEffect(() => {
    getData();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !leadsData._id) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error || "Lead not found"}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
            Lead Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customer management and payment history
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDelete}
            sx={{ borderRadius: 2 }}
          >
            Delete Lead
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={openDialog}
            sx={{ borderRadius: 2 }}
          >
            Update
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', mb: 2, fontSize: '2rem', fontWeight: 700 }}>
                {leadsData.customerName?.charAt(0) || <UserIcon />}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{leadsData.customerName || "N/A"}</Typography>
              <Chip 
                label={leadsData.status?.toUpperCase() || "NEW"} 
                color="primary" 
                size="small" 
                sx={{ mt: 1, fontWeight: 700, borderRadius: 1.5, px: 1 }} 
              />
            </Box>
            
            <Divider sx={{ mb: 2.5 }} />
            
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}><EmailIcon fontSize="small" /></Box>
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, display: 'block', lineHeight: 1 }}>EMAIL ADDRESS</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{leadsData.customerEmail || "N/A"}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}><PhoneIcon fontSize="small" /></Box>
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, display: 'block', lineHeight: 1 }}>PHONE NUMBER</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{leadsData.customerPhone || "N/A"}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}><HistoryIcon fontSize="small" /></Box>
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, display: 'block', lineHeight: 1 }}>LAST PAYMENT</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {leadsData.transactionIds?.[0]?.date 
                      ? format(new Date(leadsData.transactionIds[0].date), "MMM dd, yyyy") 
                      : "No payments yet"}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Card>
        </Grid>

        {/* Lead Stats & Actions */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Communication & Actions */}
            <Grid item xs={12} sm={6}>
              <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NoteIcon color="primary" fontSize="small" /> Communication
                </Typography>
                <Stack spacing={1.5}>
                  <Button variant="outlined" fullWidth startIcon={<EmailIcon />} sx={{ justifyContent: 'flex-start', borderRadius: 2 }}>
                    Send Email
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<PhoneIcon />} sx={{ justifyContent: 'flex-start', borderRadius: 2 }}>
                    Call Customer
                  </Button>
                </Stack>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon color="primary" fontSize="small" /> Activity Log
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {leadsData.updatedAt ? `${differenceInCalendarDays(new Date(), new Date(leadsData.updatedAt))} days ago` : "No activity"}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 48, mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 2 }}>
                  {leadsData.notes || "No internal notes available for this lead."}
                </Typography>
              </Card>
            </Grid>

            {/* Payment History Table */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentsIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>PAYMENT HISTORY</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        {headers.map(({ label, key, sortable }) => (
                          <TableCell
                            key={key}
                            onClick={sortable ? () => handleSort(key) : undefined}
                            sx={{
                              py: 1.5,
                              fontWeight: 800,
                              fontSize: "0.7rem",
                              textTransform: "uppercase",
                              letterSpacing: '0.05em',
                              cursor: sortable ? "pointer" : "default",
                              "&:hover": sortable ? { color: 'primary.main' } : {},
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {label}
                              {sortable && sortConfig.key === key && (
                                sortConfig.direction === "asc" ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((entry, index) => (
                        <TableRow key={index} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {format(new Date(entry.date), "MMM dd, yyyy · hh:mm a")}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', fontWeight: 800, color: 'primary.main' }}>
                            ₹{Number(entry.amount).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.status}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.65rem', 
                                height: 20,
                                bgcolor: entry.status === 'new' ? alpha(theme.palette.success.main, 0.05) : 'transparent',
                                color: entry.status === 'new' ? 'success.main' : 'text.secondary',
                                borderColor: entry.status === 'new' ? 'success.light' : 'divider'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={leadsData.transactionIds.length}
                  page={currentPage - 1}
                  onPageChange={(event, newPage) => setCurrentPage(newPage + 1)}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[5, 10, 20]}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setCurrentPage(1);
                  }}
                  sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                />
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Dialog for updating status and notes */}
      <Dialog 
        open={dialogOpen} 
        onClose={closeDialog}
        PaperProps={{ sx: { borderRadius: 1.5, width: '100%', maxWidth: 450 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Update Lead Status</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Lead Status</InputLabel>
              <Select
                value={status}
                label="Lead Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="asigned">Assigned</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="follow">Follow-up</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="denied">Denied</MenuItem>
              </Select>
            </FormControl>
            
            {status === "follow" && (
              <TextField
                label="Reminder Date"
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
            
            <TextField
              label="Internal Notes"
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              placeholder="Enter updates about communication with the customer..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleUpdate} variant="contained" sx={{ px: 4, borderRadius: 2 }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

